// src/components/SellerLogin.js
import React, { useState } from "react";
import "../styles/SellerLogin.css";
import config from "../config";
import { auth } from "../firebase";
import { GoogleAuthProvider, signInWithPopup, signOut } from "firebase/auth";
import SellerRegistration from "./SellerRegistration";
import PartnerPortal from "./PartnerPortal";

const provider = new GoogleAuthProvider();

export default function SellerLogin({ onBackToAdmin }) {
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const [idToken, setIdToken] = useState(null);
  const [authUser, setAuthUser] = useState(null); // backend AuthResponse
  const [application, setApplication] = useState(null);
  const [showRegistration, setShowRegistration] = useState(false);

  const [sellerApproved, setSellerApproved] = useState(false);
  const [sellerId, setSellerId] = useState(null);

  // -----------------------------
  // 1. GOOGLE LOGIN + VERIFY TOKEN
  // -----------------------------
  const handleGoogleLogin = async () => {
    try {
      setLoading(true);
      setMessage("");

      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const token = await user.getIdToken();

      const res = await fetch(`${config.baseUrl}/api/auth/verify-token`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.message || "Failed to verify token");
      }

      const data = await res.json(); // AuthResponse from backend
      setIdToken(token);
      setAuthUser(data);

      localStorage.setItem("seller_id_token", token);
      localStorage.setItem("seller_email", data.email || user.email || "");
      localStorage.setItem("seller_role", data.role || "USER");

      await loadMyApplication(token);
    } catch (err) {
      console.error("Google login error:", err);
      setMessage("❌ Google login failed: " + err.message);
      await signOut(auth).catch(() => {});
      setIdToken(null);
      setAuthUser(null);
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------
  // 2. LOAD APPLICATION STATUS
  // -----------------------------
  const loadMyApplication = async (token) => {
    setLoading(true);
    setMessage("");

    try {
      const res = await fetch(`${config.baseUrl}/api/sellers/application/me`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      if (!res.ok) {
        // Treat as NO application yet
        setApplication(null);
        setMessage(
          "You are a few steps away from selling on AuraKart. Complete your seller registration to proceed."
        );
        return;
      }

      const app = await res.json();
      setApplication(app);

      if (app.status === "PENDING") {
        setMessage(
          "✅ Registration submitted. Your seller application is under review. Please check back later."
        );
      } else if (app.status === "REJECTED") {
        setMessage(
          `❌ Your seller application was rejected.\nReason: ${
            app.adminComment || "No reason specified."
          }\nYou can update your details and re-apply.`
        );
      } else if (app.status === "APPROVED") {
        setMessage("✅ Your seller profile is approved. Loading dashboard...");
        await loadSellerProfile(token);
      }
    } catch (err) {
      console.error("loadMyApplication error:", err);
      setMessage("❌ Failed to load seller application: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------
  // 3. LOAD SELLER PROFILE (AFTER APPROVAL)
  // -----------------------------
  const loadSellerProfile = async (token) => {
    try {
      const res = await fetch(`${config.baseUrl}/api/seller/profile`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.message || "Failed to load seller profile");
      }

      const profile = await res.json(); // SellerProfileDTO
      setSellerId(profile.id);
      setSellerApproved(true);

      localStorage.setItem("seller_profile_id", profile.id);
    } catch (err) {
      console.error("loadSellerProfile error:", err);
      setMessage("❌ Failed to load seller profile: " + err.message);
      setSellerApproved(false);
    }
  };

  // -----------------------------
  // 4. SUBMIT SELLER REGISTRATION (/apply/me)
  // -----------------------------
  const handleRegistrationSubmit = async (formData) => {
    if (!idToken) {
      setMessage("Please login with Google first.");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const res = await fetch(`${config.baseUrl}/api/sellers/apply/me`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(formData),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.message || "Registration failed");
      }

      setApplication(data);
      setShowRegistration(false);
      setMessage(
        "✅ Registration submitted! Your application is now under review. Please check back later."
      );
    } catch (err) {
      console.error("Registration error:", err);
      setMessage("❌ Registration failed: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------
  // 5. LOGOUT (SELLER)
  // -----------------------------
  const handleLogout = async () => {
    await signOut(auth).catch(() => {});
    localStorage.removeItem("seller_id_token");
    localStorage.removeItem("seller_email");
    localStorage.removeItem("seller_role");
    localStorage.removeItem("seller_profile_id");

    setIdToken(null);
    setAuthUser(null);
    setApplication(null);
    setSellerApproved(false);
    setSellerId(null);
    setShowRegistration(false);
    setMessage("");
  };

  // -----------------------------
  // 6. BEFORE GOOGLE LOGIN → SHOW SIMPLE CARD
  // -----------------------------
  if (!idToken || !authUser) {
    return (
      <div className="seller-login-container">
        <div className="seller-card">
          {onBackToAdmin && (
            <button className="btn-cancel" onClick={onBackToAdmin}>
              ← Back to Admin Login
            </button>
          )}

          <h2 className="seller-title">AuraKart Seller Portal</h2>

          {message && (
            <div className="msg-box" style={{ whiteSpace: "pre-line" }}>
              {message}
            </div>
          )}

          <p style={{ marginBottom: 16 }}>
            Login with your Google account to access the seller portal.
          </p>
          <button
            className="btn-login"
            onClick={handleGoogleLogin}
            disabled={loading}
          >
            {loading ? "Signing in..." : "Continue with Google"}
          </button>
        </div>
      </div>
    );
  }

  // -----------------------------
  // 7. AFTER GOOGLE LOGIN → ALWAYS SHOW PARTNER PORTAL
  // -----------------------------
  const onboardingStatus = application?.status || "NONE";

  // If fully approved AND seller profile loaded, PartnerPortal will show normal seller view
  const isApproved =
    onboardingStatus === "APPROVED" && sellerApproved && sellerId;

  return (
    <>
      <PartnerPortal
        userRole="seller"
        sellerId={isApproved ? sellerId : null}
        onLogout={handleLogout}
        sellerOnboardingStatus={onboardingStatus}
        application={application}
        onboardingMessage={message}
        onStartRegistration={() => setShowRegistration(true)}
      />

      {showRegistration && (
        <SellerRegistration
          onSubmit={handleRegistrationSubmit}
          onCancel={() => setShowRegistration(false)}
          initialEmail={authUser?.email}
          initialContactName={authUser?.firstName}
        />
      )}
    </>
  );
}
